import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    Typography
} from "@mui/material";
import React, {useCallback, useEffect, useState} from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {useArticle} from "../firebase/useArticle";
import {useDropzone} from "react-dropzone";
import {useFirebaseNewsUpload} from "./useFirebaseNewsUpload";
import {HtmlEditor} from "../editor/HtmlEditor";


export const EditArticle = ({open, articleId, handleClose}) => {
    const {article, updateArticle} = useArticle(articleId);
    const [editArticle, setEditArticle] = useState(article);
    const {
        uploadFile,
        uploadProgress,
    } = useFirebaseNewsUpload();

    const handleInlineImageUpload = useCallback(
        (file) => {
            return new Promise((resolve) => {
                uploadFile({
                    file,
                    callback: (url) => resolve(url),
                });
            });
        },
        [uploadFile]
    );

    const onDrop = useCallback(
        (acceptedFiles) => {
            uploadFile({
                file: acceptedFiles[0],
                callback: url => {
                    setEditArticle(ea => ({
                        ...ea,
                        media: url
                    }))
                }
            })
        },
        [uploadFile]
    )

    const {
        getRootProps,
        getInputProps,
        open: openHandler
    } = useDropzone({
        onDrop: onDrop,
        multiple: false,
        accept: {"image/*": []},
        maxSize: 10 * 1024 * 1024,
    })

    useEffect(
        () => {
            if(article) {
                setEditArticle({
                    ...article,
                    text: article.text || '',
                    status: article.status || 'published',
                })
            }
        },
        [article]
    )

    const saveWithStatus = (newStatus) => {
        const publishedDate = newStatus === "published"
            ? (editArticle.published || new Date().toISOString())
            : (editArticle.published || null);

        updateArticle(
            articleId,
            {
                ...editArticle,
                text: editArticle.text || '',
                status: newStatus,
                published: publishedDate,
            }
        )
            .then(() => handleClose(true))
            .catch(() => console.error('error saving...'));
    };

    const save = () => {
        const defaultStatus = editArticle?.status || "published";
        saveWithStatus(defaultStatus);
    };

    const media = () => {
        if (uploadProgress < 101 && uploadProgress > 0) {
          return (
              <CircularProgress variant="determinate" value={uploadProgress} />
          )
        } else if(editArticle.media) {
            return (
                <img
                    src={editArticle.media}
                    alt={editArticle.title}
                    width={400}
                />
            )
        } else {
            return (
                <Typography
                    variant="h6"
                >
                    Last opp bilde
                </Typography>
            )
        }
    }

    const getContent = () => {
        if(editArticle) {
            return (
                <form noValidate autoComplete="off" onSubmit={save}>
                    <TextField
                        id="tittel"
                        label="Tittel"
                        variant="outlined"
                        fullWidth={true}
                        value={editArticle.title}
                        sx={{
                            mt: 2,
                            mb: 2
                        }}

                        onChange={event => setEditArticle(ea => ({...ea, title: event.target.value}))}
                    />
                    <Stack direction={{xs: "column", sm: "row"}} spacing={2} sx={{mb: 2.5}}>
                        <TextField
                            id="actionText"
                            label="Knappetekst / CTA (valgfri)"
                            variant="outlined"
                            fullWidth
                            placeholder="F.eks. Meld deg på kurset"
                            value={editArticle.actionText || ""}
                            onChange={event => setEditArticle(ea => ({...ea, actionText: event.target.value}))}
                            helperText="Viser en fremhevet handlingsknapp på artikkelen"
                        />
                        <TextField
                            id="actionUrl"
                            label="Handlingslenke / URL (valgfri)"
                            variant="outlined"
                            fullWidth
                            placeholder="F.eks. https://... eller mailto:kontakt@..."
                            value={editArticle.actionUrl || ""}
                            onChange={event => setEditArticle(ea => ({...ea, actionUrl: event.target.value}))}
                            helperText="Nettadresse eller mailto-lenke for handlingen"
                        />
                    </Stack>
                    <Grid
                        container={true}
                        spacing={2}
                    >
                        <Grid
                            size={{
                                xs: 12,
                                md: 8
                            }}
                        >
                            <HtmlEditor
                                value={editArticle.text}
                                onChange={data => setEditArticle(ea => ({...ea, text: data}))}
                                onImageUpload={handleInlineImageUpload}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 4
                            }}
                        >
                            <Stack
                                spacing={2}
                                alignItems={"flex-start"}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Fremhevet bannerbilde (vises øverst på kortet). Flere bilder og formatering kan også settes inn direkte i teksten til venstre via bildeknappen i verktøylinjen.
                                </Typography>
                                <Box
                                    {...getRootProps()}
                                >
                                    <input {...getInputProps()} />
                                    {media()}
                                </Box>
                                <Stack
                                    direction={"row"}
                                    spacing={2}
                                >
                                    <Button
                                        variant={'outlined'}
                                        disabled={!editArticle.media}
                                        onClick={() => {
                                            setEditArticle(ea => ({...ea, media: null}))
                                        }}
                                    >
                                        Fjern bilde
                                    </Button>
                                    <Button
                                        variant={"outlined"}
                                        onClick={openHandler}
                                    >
                                        Velg bilde
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>

                    </Grid>
                </form>
            )
        } else {
            return null
        }
    }

    const isPublished = editArticle?.status === "published";

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={"xl"}
            fullWidth={true}
            aria-labelledby="edit-article-dialog-title"
        >
            <DialogTitle id="edit-article-dialog-title" sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <Typography variant="h6" component="span" sx={{fontWeight: 700}}>
                    {articleId ? "Rediger nyhetsartikkel" : "Opprett nyhetsartikkel"}
                </Typography>
                <Chip
                    label={isPublished ? "Publisert" : "Utkast"}
                    color={isPublished ? "success" : "warning"}
                    variant="outlined"
                    size="small"
                />
            </DialogTitle>
            <DialogContent
                sx={{
                    minHeight: "50dvh"
                }}
            >
                {getContent()}
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2, flexWrap: "wrap", gap: 1}}>
                <Button onClick={() => handleClose(false)} color={"inherit"}>
                    Avbryt
                </Button>
                <Button
                    variant={"outlined"}
                    color={"warning"}
                    onClick={() => saveWithStatus("draft")}
                >
                    {isPublished ? "Trekk tilbake (gjør til utkast)" : "Lagre som utkast"}
                </Button>
                <Button
                    variant={"contained"}
                    color={"primary"}
                    onClick={() => saveWithStatus("published")}
                >
                    {isPublished ? "Lagre endringer" : "Publiser nå"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}